// src/pages/HomePage.js
//
// The personal home page, /home (UI revamp step 2; UI_REVAMP_ENABLED only).
// The left sidebar is the one this role has on its original page (Admin
// Dashboard's for admins, Coordinator page's for coordinators;
// config/homeShortcuts.js). Then "Good morning, <name>" and what's yours: this
// week's visits on the Research Calendar, your coordinator boxes, the studies
// you're Lead/Backup CRC on, this month's counts, and your weekly report draft.
//
// Admins and owners (step 3) get instead of the report card: "Needs your
// attention" (study numbers to approve, enrollments waiting, goal points to
// approve), today's visits for every coordinator, the Admin Dashboard's study
// numbers, Review PFT Patients / Raw Database as tabs with their counts, the
// newest submitted weekly reports, and staff credentials expiring or overdue.
//
// Read-only: it uses the same loaders the existing pages use and writes
// nothing but your own things: a name you type (your browser) and My notes /
// My quick links incl. pinned pages (saveHomePrivate).
// Every section shows only for roles whose pages it links to, and loads on its
// own: a section that fails says so, the rest still work.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { Activity, ChevronDown, ChevronLeft, ChevronRight, Database, History, LogOut, Menu, Pencil, X } from "lucide-react";
import { navIcon } from "../components/home/navIcons";
import { PFT_IMPORT_ENABLED } from "../config/featureFlags";
import { loadStudiesNeedingApproval } from "../services/studyReportApprovalService";
import { fetchPendingEnrollmentRequests } from "../services/enrollmentRequestService";
import { loadGoalPointData } from "../services/goalPointService";
import { loadEmployeeCredentials, loadGoalCircles, loadGoalHistory } from "../services/homeAdminService";
import { GoalCircle, GoalHistoryModal } from "../components/coordinator/GoalWidgets";
import { daysLeftInfo } from "../services/goalProgress";
import { periodText } from "../components/weeklyReport/ui";
import { reportByline } from "../components/weeklyReport/ReportPatientsList";
import {
  attentionItems, credentialAlerts, goalPointCoordinators, latestReports, studyCounts,
  todaysVisits,
} from "../services/homeAdmin";
import AnnouncementsPill from "../components/home/AnnouncementsPill";
import { logoutAndClearSite } from "../services/logout";
import { db } from "../firebase";
import { useTenant } from "../context/TenantContext";
import { effectiveRole } from "../services/membershipService";
import { fetchCoordinators } from "../services/coordinatorService";
import { fetchStudies } from "../services/studyService";
import { loadCrcNames } from "../services/crcNamesService";
import { listCalendarEvents } from "../services/researchCalendarService";
import { loadReportSearchPatients } from "../services/reportSearchPatients";
import { loadAllReportPatients, loadDraft } from "../services/weeklyReportService";
import { draftIsEmpty, countValue } from "../services/weeklyReportModel";
import { segmentByKey } from "../services/reportPeriods";
import { getEnrollmentPeriodPath, getScreeningPeriodPath } from "../services/firestorePaths";
import { monthKey, PERIOD_TYPES, currentPeriodKey, periodLabel } from "../services/enrollmentPeriods";
import { patientInitials } from "../services/patientInitials";
import { HOME_SHORTCUT_GROUPS, navFor, pinnableFor } from "../config/homeShortcuts";
import SidebarThemeToggle from "../components/SidebarThemeToggle";
import useHomePrivate from "../components/home/useHomePrivate";
import MyNotesCard from "../components/home/MyNotesCard";
import MyLinksCard from "../components/home/MyLinksCard";
import {
  greetingFor, greetingName, homeNameKey, myBoxes, myNames, myStudies, myWeekVisits, rosterEntryFor, weekStartOf,
} from "../services/homeData";

// Every link from here says it came from home, so pages that pick their Back
// destination (the patient page, Review PFT Patients) come back to /home.
// Pages that use the browser's Back already do.
const FROM_HOME = { from: "home" };

const readStored = (key) => {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
};
const writeStored = (key, value) => {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    // Unavailable storage: the name just isn't remembered.
  }
};

// One fetch per section: { loading, data, error }.
const useLoad = (enabled, load, deps) => {
  const [state, setState] = useState({ loading: !!enabled, data: null, error: "" });
  useEffect(() => {
    if (!enabled) {
      setState({ loading: false, data: null, error: "" });
      return undefined;
    }
    let cancelled = false;
    setState({ loading: true, data: null, error: "" });
    load()
      .then((data) => !cancelled && setState({ loading: false, data, error: "" }))
      .catch((e) => {
        console.error("Home section failed to load:", e);
        if (!cancelled) setState({ loading: false, data: null, error: "Couldn't load this right now." });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  // Just switched on (e.g. the roster has just matched) but the load hasn't
  // started yet: that render must read as loading, never as "no data".
  if (enabled && state.data === null && !state.error) return { ...state, loading: true };
  return state;
};

const Section = ({ title, action, children, label }) => (
  <section className="rx-home-card" aria-label={label || title}>
    <div className="flex items-baseline justify-between gap-3 mb-3">
      <h2 className="rx-home-title">{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

const SectionLink = ({ to, children }) => (
  <Link to={to} state={FROM_HOME} className="rx-home-link inline-flex items-center gap-0.5">
    {children}
    <ChevronRight className="w-3.5 h-3.5" />
  </Link>
);

const Muted = ({ children }) => <p className="rx-home-muted text-sm">{children}</p>;

const Loading = () => <Muted>Loading…</Muted>;

// A count of 0 shows as a dash (user, 2026-10-30).
const countOrDash = (n) => (n ? n : "—");

const fmtDay = (d) => d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
const fmtTime = (d) => d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

/* -------- Left sidebar: logo, shortcuts, light/dark switch --------
   Grouped under the Coordinator page sidebar's own headings (Operations,
   Patient Flow, Navigation, System) so the two read the same; collapsed, the
   groups are just separated by space. */
const NavItem = ({ label, icon: Icon, tone, onClick, open }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    className={`group relative w-full flex items-center rounded-lg border border-transparent rx-home-navitem ${
      open ? "gap-2.5 px-2 py-1.5 justify-start" : "p-1.5 justify-center"
    }`}
  >
    {/* Same tile classes as the other sidebars, so it takes the same per-item
        colour (theme-v2.css, sidebar tiles). */}
    <span className={`flex items-center justify-center w-7 h-7 rounded-md bg-gradient-to-br border shrink-0 text-${tone}-100`}>
      <Icon className="w-3.5 h-3.5" />
    </span>
    {open && <span className="text-xs font-medium truncate">{label}</span>}
  </button>
);

const NavGroup = ({ label, open, children }) => (
  <div>
    {open ? (
      <div className="px-1.5 mb-1.5">
        <span className="text-[9px] uppercase tracking-[0.22em] text-slate-400 font-semibold">{label}</span>
      </div>
    ) : (
      <div className="h-2" aria-hidden="true" />
    )}
    <div className="space-y-0.5">{children}</div>
  </div>
);

const HomeSidebar = ({ nav, onOpen, onLogout, open, setOpen, mobileOpen, setMobileOpen }) => (
  <>
    {mobileOpen && (
      <div className="lg:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setMobileOpen(false)} aria-hidden="true" />
    )}
    <aside
      aria-label="Shortcuts"
      className={`rx-home-sidebar fixed lg:sticky top-0 z-50 h-screen flex flex-col border-r transition-all duration-300 ease-out ${
        open ? "w-64" : "w-[72px]"
      } ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
    >
      <div className={`flex p-3 border-b ${open ? "items-center justify-between gap-2" : "flex-col items-center gap-2"}`}>
        <img
          src="/RegX zoom.png"
          alt="RegX"
          className={open ? "h-12 w-auto max-w-[9rem] object-contain object-left" : "w-11 h-11 object-contain"}
        />
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          title={open ? "Collapse" : "Expand"}
          aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
          className="hidden lg:flex w-7 h-7 rounded-lg items-center justify-center rx-home-muted"
        >
          {open ? <ChevronLeft className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
          className="lg:hidden w-7 h-7 rounded-lg flex items-center justify-center rx-home-muted"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-2 px-2 space-y-3">
        {HOME_SHORTCUT_GROUPS.map((group) => {
          const items = nav.items.filter((s) => s.group === group);
          if (!items.length) return null;
          return (
            <NavGroup key={group} label={group} open={open}>
              {items.map((s) => (
                <NavItem
                  key={s.id}
                  label={s.label}
                  icon={navIcon(s.icon)}
                  tone={s.tone}
                  open={open}
                  onClick={() => {
                    onOpen(s);
                    setMobileOpen(false);
                  }}
                />
              ))}
            </NavGroup>
          );
        })}
        <NavGroup label="System" open={open}>
          {nav.system.map((s) => (
            <NavItem
              key={s.id}
              label={s.label}
              icon={navIcon(s.icon)}
              tone={s.tone}
              open={open}
              onClick={() => {
                onOpen(s);
                setMobileOpen(false);
              }}
            />
          ))}
          <NavItem label="Log Out" icon={LogOut} tone="red" open={open} onClick={onLogout} />
        </NavGroup>
        <SidebarThemeToggle expanded={open} />
      </nav>
    </aside>
  </>
);

/* -------- Admin: the two patient queues as tabs --------
   Review PFT Patients and Raw Database, each with a "Review patients" button.
   No counts (user, 2026-11-02): nothing here reads either list. */
const PatientQueuesCard = ({ showPft }) => {
  const tabs = [
    showPft && {
      id: "pft",
      label: "PFT",
      about: "Patients found in extracted PFTs, before they reach the patient list.",
      to: "/coordinator/pft-uploads",
      icon: Activity,
    },
    {
      id: "raw",
      label: "Raw Database",
      about: "Everything patients submitted through their referral text link.",
      to: "/coordinator/raw-database",
      icon: Database,
    },
  ].filter(Boolean);
  const [tabId, setTabId] = useState(tabs[0].id);
  const tab = tabs.find((t) => t.id === tabId) || tabs[0];
  const Icon = tab.icon;
  return (
    <section className="rx-home-card" aria-label="Patients to review">
      <div role="tablist" aria-label="Patients to review" className="rx-home-tabs mb-3">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={t.id === tab.id}
            onClick={() => setTabId(t.id)}
            className={`rx-home-tab ${t.id === tab.id ? "rx-home-tab--on" : ""}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" aria-label={tab.label}>
        <div className="flex items-start gap-3">
          <span className="rx-home-pft-icon" aria-hidden="true">
            <Icon className="w-4 h-4" />
          </span>
          <Muted>{tab.about}</Muted>
        </div>
        <Link to={tab.to} state={FROM_HOME} className="rx-home-button inline-flex mt-4">
          Review patients
        </Link>
      </div>
    </section>
  );
};

/* -------- Admin: Goal Progress, as on the CRC page --------
   The same circles (GoalWidgets), the same records and rules (homeAdmin
   goalSnapshot), the same History (buildGoalHistory). Picking a month in
   History shows it here, as there. Editing a goal happens in one place, the
   CRC page's own Edit goal pop-up (goal + rules), so both pages always agree. */
const GoalProgressCard = ({ onEditGoal }) => {
  const [monthId, setMonthId] = useState(() => currentPeriodKey(PERIOD_TYPES.MONTH));
  const circles = useLoad(true, () => loadGoalCircles(monthId), [monthId]);
  const [history, setHistory] = useState({ open: false, loading: false, error: "", rows: [] });
  const days = daysLeftInfo(monthId);
  const openHistory = async () => {
    setHistory({ open: true, loading: true, error: "", rows: [] });
    try {
      const rows = await loadGoalHistory((circles.data && circles.data.names) || []);
      setHistory({ open: true, loading: false, error: "", rows });
    } catch (e) {
      console.error("Couldn't load goal history:", e);
      setHistory({ open: true, loading: false, error: "Couldn't load the history right now.", rows: [] });
    }
  };
  return (
    <section className="rx-home-card" aria-label="Goal Progress">
      <div className="flex items-center gap-2 mb-1">
        <h2 className="rx-home-title">Goal Progress</h2>
        <button
          type="button"
          onClick={openHistory}
          className="rx-icon-button ml-auto"
          title="Past months for both goals"
          aria-label="History"
          disabled={circles.loading}
        >
          <History className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        <span className="text-[11px] font-semibold rx-home-muted">{periodLabel(PERIOD_TYPES.MONTH, monthId) || monthId}</span>
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-400/10 border border-amber-400/20 text-[10px] text-amber-700 rx-goal-days">
          {days.kind === "past" ? "Month ended" : days.kind === "future" ? "Not started" : `${days.days} day${days.days !== 1 ? "s" : ""} left`}
        </span>
      </div>
      {circles.loading ? (
        <Loading />
      ) : circles.error ? (
        <Muted>{circles.error}</Muted>
      ) : (
        <div className="grid grid-cols-2 gap-2 coordinator-utility-panel">
          <GoalCircle
            metric="enrollments"
            label="Randomizations"
            total={circles.data.enrollments.total}
            goal={circles.data.enrollments.goal}
            pct={circles.data.enrollments.pct}
            canEditGoal
            onEditGoal={() => onEditGoal("enrollments", monthId)}
          />
          <GoalCircle
            metric="screenings"
            label="Screenings"
            total={circles.data.screenings.total}
            goal={circles.data.screenings.goal}
            pct={circles.data.screenings.pct}
            canEditGoal
            onEditGoal={() => onEditGoal("screenings", monthId)}
          />
        </div>
      )}
      <GoalHistoryModal
        open={history.open}
        loading={history.loading}
        error={history.error}
        rows={history.rows}
        selectedMonthId={monthId}
        onPick={(id) => {
          setMonthId(id);
          setHistory((h) => ({ ...h, open: false }));
        }}
        onClose={() => setHistory((h) => ({ ...h, open: false }))}
      />
    </section>
  );
};

/* -------- One coordinator box: collapsed by default -------- */
const HomeBox = ({ stage, patients }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rx-home-box">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="rx-home-row rx-home-row--link w-full text-left"
      >
        <span className="text-sm font-semibold flex-1">{stage}</span>
        <span className="rx-home-muted text-sm tabular-nums">{patients.length}</span>
        <ChevronDown className={`w-4 h-4 rx-home-muted transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open &&
        (patients.length === 0 ? (
          <div className="px-1 pb-2">
            <Muted>Nobody in this box.</Muted>
          </div>
        ) : (
          <ul className="rx-home-list pl-1">
            {patients.map((p) => (
              <li key={`${p.source}:${p.id}`}>
                <Link
                  to={`/coordinator/patient/${p.id}?src=${p.source}`}
                  state={FROM_HOME}
                  className="rx-home-row rx-home-row--link"
                >
                  <span className="rx-home-initials" aria-hidden="true">
                    {patientInitials(p.name)}
                  </span>
                  <span className="text-sm font-medium truncate flex-1">{p.name || "Unnamed"}</span>
                  <span className="rx-home-muted text-xs font-mono">{p.mrn}</span>
                </Link>
              </li>
            ))}
          </ul>
        ))}
    </div>
  );
};

export default function HomePage() {
  const navigate = useNavigate();
  const { user, access, tenantId, siteId } = useTenant();
  const role = effectiveRole(access, tenantId, siteId);
  const is = (...roles) => role === "owner" || roles.includes(role);
  const canCalendar = is("admin", "coordinator");
  const canPatients = is("admin", "coordinator", "referral");
  const canReports = is("admin", "coordinator", "referral");
  const isAdmin = is("admin");
  const email = user?.email || "";
  const uid = user?.uid || "";

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  // My notes + My quick links: one private doc per person per site.
  const privateStore = useHomePrivate({ tenantId, siteId, uid });

  // Who am I on the coordinator roster (by login email)?
  const roster = useLoad(!!email, () => fetchCoordinators(), [email, tenantId, siteId]);
  const me = useMemo(() => rosterEntryFor(roster.data, email), [roster.data, email]);
  const names = useMemo(() => myNames(me), [me]);
  // The coordinator sections (This week, My patients, My studies) are for
  // coordinators. Admins never get them, even when their email is on a site's
  // coordinator list (it is on Ohio's), so every site's admin home is the same.
  const onRoster = names.size > 0 && !isAdmin;

  // Greeting name: roster, account, or what they typed here.
  const nameKey = uid ? homeNameKey(uid, tenantId, siteId) : "";
  const [typedName, setTypedName] = useState(() => (nameKey ? readStored(nameKey) : ""));
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  useEffect(() => {
    setTypedName(nameKey ? readStored(nameKey) : "");
  }, [nameKey]);
  const name = greetingName({ rosterName: me?.name, authName: user?.displayName, typedName });
  const nameIsTyped = !me?.name && !user?.displayName;
  const saveName = (e) => {
    e.preventDefault();
    const v = nameDraft.trim().slice(0, 40);
    writeStored(nameKey, v);
    setTypedName(v);
    setEditingName(false);
  };

  const now = useMemo(() => new Date(), []);
  const weekStart = useMemo(() => weekStartOf(now), [now]);
  const month = monthKey(now);

  const visits = useLoad(
    canCalendar && onRoster,
    async () => {
      const end = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
      const events = await listCalendarEvents(weekStart.toISOString(), end.toISOString());
      return myWeekVisits(events, { email, names }, weekStart);
    },
    [canCalendar, onRoster, email, names, weekStart]
  );
  // Lead CRC on studies is a full name; the CRC names list says who it is.
  // If the list can't be read, exact names still match (fail-soft).
  const studies = useLoad(
    onRoster,
    async () => {
      const [list, links] = await Promise.all([fetchStudies(), loadCrcNames().catch(() => [])]);
      return myStudies(list, names, { links, roster: roster.data || [] });
    },
    [onRoster, names, me]
  );
  const boxes = useLoad(
    canPatients && onRoster,
    async () => {
      const { patients, sheetError } = await loadReportSearchPatients();
      return { boxes: myBoxes(patients, names), sheetError };
    },
    [canPatients, onRoster, names]
  );
  const counts = useLoad(
    onRoster && !isAdmin,
    async () => {
      const read = async (path) => {
        const snap = await getDoc(doc(db, path, me.name));
        return snap.exists() ? Number(snap.data().count) || 0 : 0;
      };
      const [randomizations, screenings] = await Promise.all([
        read(getEnrollmentPeriodPath(month)),
        read(getScreeningPeriodPath(month)).catch(() => null),
      ]);
      return { randomizations, screenings };
    },
    [onRoster, isAdmin, me?.name, month]
  );
  // The weekly report draft (owner-only by rule), or "none" when there isn't one.
  const draft = useLoad(canReports && !isAdmin && !!uid, async () => (await loadDraft(uid)) || "none", [canReports, isAdmin, uid]);

  // Admins: what's waiting for them, each part loaded on its own (fail-soft).
  const allStudies = useLoad(isAdmin, () => fetchStudies(), [isAdmin, tenantId, siteId]);
  const approvals = useLoad(isAdmin, () => loadStudiesNeedingApproval(), [isAdmin, tenantId, siteId]);
  const requests = useLoad(isAdmin, () => fetchPendingEnrollmentRequests(), [isAdmin, tenantId, siteId]);
  const goalPoints = useLoad(isAdmin, async () => goalPointCoordinators(await loadGoalPointData()), [isAdmin, tenantId, siteId]);
  // Admin snippets (2026-11-02), each loaded on its own, each with its page's rule.
  const credentials = useLoad(isAdmin, async () => credentialAlerts(await loadEmployeeCredentials()), [isAdmin, tenantId, siteId]);
  const todayVisits = useLoad(
    isAdmin,
    async () => {
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
      return todaysVisits(await listCalendarEvents(start.toISOString(), end.toISOString()), now);
    },
    [isAdmin, tenantId, siteId, now]
  );
  const reports = useLoad(isAdmin, async () => latestReports(await loadAllReportPatients(), 5), [isAdmin, tenantId, siteId]);
  const attention = useMemo(
    () => attentionItems({ approvals, studies: allStudies, requests, goalPoints }),
    [approvals, allStudies, requests, goalPoints]
  );
  const studyNumbers = useMemo(() => (allStudies.data ? studyCounts(allStudies.data) : null), [allStudies.data]);

  // The left sidebar: the same one this role has on its original page.
  const nav = navFor(role);
  const hasNav = nav.items.length > 0 || nav.system.length > 0;
  const openShortcut = (s) => {
    if (s.newTab) window.open(s.route, "_blank", "noopener");
    else navigate(s.route, { state: { ...FROM_HOME, ...(s.state || {}) } });
  };
  const handleLogout = async () => {
    await logoutAndClearSite();
    navigate("/"); // "/" redirects to /login, as on the Coordinator page
  };
  const showMine = canCalendar || canPatients || canReports;

  const draftSummary = useMemo(() => {
    const d = draft.data && draft.data !== "none" ? draft.data.draft : null;
    if (!d || draftIsEmpty(d)) return null;
    const people = (d.studies || []).reduce((n, s) => n + (s.patients || []).length, 0);
    return {
      period: segmentByKey(d.segmentKey)?.label || "",
      studies: d.studies || [],
      people,
      preScreens: countValue(d.counts?.preScreens),
      savedAt: draft.data.updatedAtMs ? new Date(draft.data.updatedAtMs) : null,
    };
  }, [draft.data]);

  return (
    <div className="rx-home min-h-screen flex">
      {hasNav && (
        <HomeSidebar
          nav={nav}
          onOpen={openShortcut}
          onLogout={handleLogout}
          open={sidebarOpen}
          setOpen={setSidebarOpen}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
        />
      )}

      <main className="flex-1 min-w-0 px-4 sm:px-8 py-10 sm:py-14">
        <div className="max-w-5xl mx-auto">
          <header className="mb-8 sm:mb-10">
            {hasNav && (
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
                className="lg:hidden mb-4 w-9 h-9 rounded-xl flex items-center justify-center rx-home-muted"
              >
                <Menu className="w-4 h-4" />
              </button>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="rx-home-date text-sm">
                {now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
              </p>
              {uid && (
                <AnnouncementsPill
                  tenantId={tenantId}
                  siteId={siteId}
                  uid={uid}
                  onPost={isAdmin ? () => navigate("/announcements", { state: FROM_HOME }) : undefined}
                />
              )}
            </div>
            <h1 className="rx-home-greeting mt-1">
              {greetingFor(now)}
              {name ? `, ${name}` : ""}
              {name && nameIsTyped && !editingName && (
                <button
                  type="button"
                  className="rx-icon-button ml-2 align-middle"
                  aria-label="Change the name we call you"
                  title="Change the name we call you"
                  onClick={() => {
                    setNameDraft(typedName);
                    setEditingName(true);
                  }}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </h1>
            {(editingName || (!name && !roster.loading && !!nameKey)) && (
              <form onSubmit={saveName} className="mt-3 flex flex-wrap items-center gap-2">
                <label htmlFor="rx-home-name" className="rx-home-muted text-sm">
                  What should we call you?
                </label>
                <input
                  id="rx-home-name"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  maxLength={40}
                  className="rx-home-input"
                  placeholder="First name"
                />
                <button type="submit" className="rx-home-button">
                  Save
                </button>
                {editingName && (
                  <button type="button" className="rx-home-link" onClick={() => setEditingName(false)}>
                    Cancel
                  </button>
                )}
              </form>
            )}
          </header>

          {/* Admins aren't expected to be on the roster: no note for them. */}
          {showMine && !isAdmin && !roster.loading && !onRoster && email && (
            <div className="rx-home-note mb-6 text-sm">
              Your login isn't on the coordinator roster for this site, so your own visits, patients and studies
              can't be shown here. An admin can add your email under Manage Coordinators.
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-5">
              {isAdmin && (
                <Section title="Needs your attention" action={<SectionLink to="/dashboard">Admin Dashboard</SectionLink>}>
                  {attention.items.length > 0 && (
                    <ul className="rx-home-list">
                      {attention.items.map((item) =>
                        item.studies ? (
                          <li key={item.id} className="rx-home-attn">
                            <p className="text-sm font-medium">{item.text}</p>
                            <ul className="mt-1">
                              {item.studies.map((s) => (
                                <li key={s.id}>
                                  <Link to={`/study/${s.id}`} state={FROM_HOME} className="rx-home-row rx-home-row--link">
                                    <span className="text-sm truncate flex-1">{s.name}</span>
                                    <ChevronRight className="w-3.5 h-3.5 rx-home-muted" />
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </li>
                        ) : (
                          <li key={item.id} className="rx-home-attn">
                            <Link to={item.to} state={{ ...FROM_HOME, ...item.state }} className="rx-home-row rx-home-row--link">
                              <span className="text-sm font-medium flex-1">{item.text}</span>
                              <ChevronRight className="w-3.5 h-3.5 rx-home-muted" />
                            </Link>
                          </li>
                        )
                      )}
                    </ul>
                  )}
                  {attention.pending && <Loading />}
                  {!attention.pending && attention.items.length === 0 && attention.failed.length === 0 && (
                    <Muted>Nothing waiting for you right now.</Muted>
                  )}
                  {attention.failed.length > 0 && (
                    <p className="rx-home-warn text-xs mt-2">Couldn't check {attention.failed.join(", ")} right now.</p>
                  )}
                </Section>
              )}

              {/* Admins: the CRC page's Goal Progress circles, same records (an edit on
                  either page shows on both); the pencil opens that page's Edit goal. */}
              {isAdmin && (
                <GoalProgressCard
                  onEditGoal={(metric, monthId) =>
                    navigate("/coordinator", { state: { ...FROM_HOME, goalEdit: { metric, monthId } } })
                  }
                />
              )}

              {/* Admins: today's visits for every coordinator (the Research Calendar). */}
              {isAdmin && (
                <Section title="Today's visits" action={<SectionLink to="/research-calendar">Calendar</SectionLink>}>
                  {todayVisits.loading ? (
                    <Loading />
                  ) : todayVisits.error ? (
                    <Muted>{todayVisits.error}</Muted>
                  ) : todayVisits.data.length === 0 ? (
                    <Muted>No visits on the Research Calendar today.</Muted>
                  ) : (
                    <ul className="rx-home-list">
                      {todayVisits.data.slice(0, 8).map((v) => (
                        <li key={v.id || `${v.start}-${v.summary}`} className="rx-home-row">
                          <div className="rx-home-when">
                            <div className="text-xs font-semibold">{fmtTime(v.startDate)}</div>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">{v.patientName || v.summary || "Visit"}</div>
                            <div className="rx-home-muted text-xs truncate">
                              {[v.study, v.visit, v.room === "Other" ? v.otherRoom : v.room].filter(Boolean).join(" · ")}
                            </div>
                          </div>
                          {v.coordinatorName && (
                            <span className="rx-home-muted text-xs truncate max-w-[8rem]">{v.coordinatorName}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {todayVisits.data && todayVisits.data.length > 8 && (
                    <p className="rx-home-muted text-[11px] mt-2">+{todayVisits.data.length - 8} more on the calendar</p>
                  )}
                  <p className="rx-home-muted text-[11px] mt-3">
                    Every visit on the Research Calendar today. Visits added straight in Google Calendar show without study
                    or coordinator details.
                  </p>
                </Section>
              )}

              {/* Admins: the newest submitted weekly reports (Admin Reports → Submitted reports). */}
              {isAdmin && (
                <Section
                  title="Submitted weekly reports"
                  action={<SectionLink to="/admin/weekly-reports">All reports</SectionLink>}
                >
                  {reports.loading ? (
                    <Loading />
                  ) : reports.error ? (
                    <Muted>{reports.error}</Muted>
                  ) : reports.data.length === 0 ? (
                    <Muted>No weekly reports submitted yet.</Muted>
                  ) : (
                    <ul className="rx-home-list">
                      {reports.data.map((r) => (
                        <li key={r.id} className="rx-home-row">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">
                              {r.coordinator || "Unknown"} <span className="rx-home-muted font-normal">· {periodText(r)}</span>
                            </div>
                            {/* No email on the home page (user, 2026-11-03). */}
                            <div className="rx-home-muted text-xs break-words">{reportByline({ ...r, authorEmail: "" })}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>
              )}

              {canCalendar && onRoster && (
                <Section title="This week" action={<SectionLink to="/research-calendar">Calendar</SectionLink>}>
                  {visits.loading ? (
                    <Loading />
                  ) : visits.error ? (
                    <Muted>{visits.error}</Muted>
                  ) : visits.data.length === 0 ? (
                    <Muted>No visits on the Research Calendar for you this week.</Muted>
                  ) : (
                    <ul className="rx-home-list">
                      {visits.data.map((v) => (
                        <li key={v.id || `${v.start}-${v.summary}`} className="rx-home-row">
                          <div className="rx-home-when">
                            <div className="text-xs font-semibold">{fmtDay(v.startDate)}</div>
                            <div className="rx-home-muted text-xs">{fmtTime(v.startDate)}</div>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">{v.patientName || v.summary || "Visit"}</div>
                            <div className="rx-home-muted text-xs truncate">
                              {[v.study, v.visit, v.room === "Other" ? v.otherRoom : v.room].filter(Boolean).join(" · ")}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="rx-home-muted text-[11px] mt-3">
                    Visits entered straight into Google Calendar without a coordinator don't show here.
                  </p>
                </Section>
              )}

              {canPatients && onRoster && (
                <Section title="My patients" action={<SectionLink to="/coordinator">Coordinator</SectionLink>}>
                  {boxes.loading ? (
                    <Loading />
                  ) : boxes.error ? (
                    <Muted>{boxes.error}</Muted>
                  ) : (
                    <div className="space-y-1">
                      {boxes.data.sheetError && (
                        <p className="rx-home-warn text-xs mb-2">
                          The patient sheet couldn't be read, so this list may be short.
                        </p>
                      )}
                      {boxes.data.boxes.map((b) => (
                        <HomeBox key={b.stage} stage={b.stage} patients={b.patients} />
                      ))}
                    </div>
                  )}
                </Section>
              )}

              {onRoster && (
                <Section title="My studies" action={<SectionLink to="/studies">Studies</SectionLink>}>
                  {studies.loading ? (
                    <Loading />
                  ) : studies.error ? (
                    <Muted>{studies.error}</Muted>
                  ) : studies.data.length === 0 ? (
                    <Muted>You're not Lead or Backup CRC on any study.</Muted>
                  ) : (
                    <ul className="rx-home-list">
                      {/* Not links: the study detail page is meant for admins
                          (user, 2026-10-30). "Studies" above opens the list. */}
                      {studies.data.map(({ study, role: crcRole }) => (
                        <li key={study.id} className="rx-home-row">
                          <span className="text-sm font-medium truncate flex-1">{study.NAME || study.STUDY_ID || "Study"}</span>
                          <span className="rx-home-muted text-xs">{crcRole}</span>
                          <span className="rx-home-muted text-xs w-24 text-right truncate">{study.STATUS || ""}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>
              )}
            </div>

            <div className="space-y-5">
              {/* Admins: staff credentials expired or expiring (the Notifications page's rule). */}
              {isAdmin && (
                <Section title="Staff credentials" action={<SectionLink to="/notifications">Notifications</SectionLink>}>
                  {credentials.loading ? (
                    <Loading />
                  ) : credentials.error ? (
                    <Muted>{credentials.error}</Muted>
                  ) : credentials.data.total === 0 ? (
                    <Muted>Every credential is up to date.</Muted>
                  ) : (
                    <>
                      <p className="rx-home-muted text-xs mb-1">
                        {credentials.data.urgent > 0 ? `${credentials.data.urgent} need action now · ` : ""}
                        {credentials.data.total} expiring or overdue
                      </p>
                      <ul className="rx-home-list">
                        {credentials.data.rows.slice(0, 5).map((c) => (
                          <li key={c.key} className="rx-home-row">
                            <span className={`rx-cred-dot rx-cred-dot--${c.severity}`} aria-hidden="true" />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium truncate">{c.name}</div>
                              <div className="rx-home-muted text-xs truncate">
                                {c.label} · {c.short}
                                {c.value && c.value !== "N/A" ? ` · ${c.value}` : ""}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                      {credentials.data.total > 5 && (
                        <p className="rx-home-muted text-[11px] mt-2">+{credentials.data.total - 5} more on Notifications</p>
                      )}
                    </>
                  )}
                </Section>
              )}

              {uid && <MyNotesCard store={privateStore} />}

              {/* One card: this month's counts on top, then the report draft
                  (user, 2026-10-30: keep the right column short). Not for
                  admins (user, 2026-11-02: they don't file reports). */}
              {!isAdmin && (canReports || onRoster) && (
                <Section title="Weekly report" label="Weekly report">
                  {onRoster && (
                    <div aria-label="This month" role="group" className="mb-4 pb-4 rx-home-split">
                      <p className="rx-home-muted text-xs mb-1.5">This month</p>
                      {counts.loading ? (
                        <Loading />
                      ) : counts.error ? (
                        <Muted>{counts.error}</Muted>
                      ) : (
                        <div className="flex gap-8">
                          <div>
                            <div className="rx-home-stat">{countOrDash(counts.data.randomizations)}</div>
                            <div className="rx-home-muted text-xs">Randomizations</div>
                          </div>
                          <div>
                            <div className="rx-home-stat">{countOrDash(counts.data.screenings)}</div>
                            <div className="rx-home-muted text-xs">Screenings</div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {!canReports ? null : draft.loading ? (
                    <Loading />
                  ) : draft.error ? (
                    <Muted>{draft.error}</Muted>
                  ) : !draftSummary ? (
                    <>
                      <Muted>
                        You can start drafting your report throughout the week and submit it at the end of the week.
                      </Muted>
                      <Link to="/weekly-survey" state={FROM_HOME} className="rx-home-button inline-flex mt-3">
                        Start drafting
                      </Link>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium">Draft in progress{draftSummary.period ? ` · ${draftSummary.period}` : ""}</p>
                      <p className="rx-home-muted text-xs mt-0.5">
                        {draftSummary.studies.length} {draftSummary.studies.length === 1 ? "study" : "studies"} ·{" "}
                        {draftSummary.people} {draftSummary.people === 1 ? "person" : "people"}
                        {draftSummary.preScreens ? ` · ${draftSummary.preScreens} pre-screens not tied to a study` : ""}
                      </p>
                      {draftSummary.studies.length > 0 && (
                        <ul className="rx-home-list mt-2">
                          {draftSummary.studies.slice(0, 5).map((s) => (
                            <li key={s.studyDocId} className="rx-home-row">
                              <span className="text-sm truncate flex-1">{s.name || s.studyId || "Study"}</span>
                              <span className="rx-home-muted text-xs tabular-nums">{(s.patients || []).length}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {draftSummary.savedAt && (
                        <p className="rx-home-muted text-[11px] mt-2">
                          Saved {draftSummary.savedAt.toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" })}
                        </p>
                      )}
                      <Link to="/weekly-survey" state={FROM_HOME} className="rx-home-button inline-flex mt-3">
                        Continue draft
                      </Link>
                    </>
                  )}
                </Section>
              )}

              {uid && <MyLinksCard store={privateStore} pinnable={pinnableFor(role)} onOpenPage={openShortcut} />}

              {/* Admins, at the bottom of the right column: the Dashboard's study
                  numbers, then the two patient queues (Review PFT Patients / Raw
                  Database) as tabs. */}
              {isAdmin && (
                <Section title="Studies" action={<SectionLink to="/dashboard">All studies</SectionLink>}>
                  {allStudies.loading ? (
                    <Loading />
                  ) : allStudies.error ? (
                    <Muted>{allStudies.error}</Muted>
                  ) : (
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3" role="group" aria-label="Study numbers">
                      {[
                        ["Total", studyNumbers.total],
                        ["Active", studyNumbers.active],
                        ["Start-up", studyNumbers.startup],
                        ["Closed / closing", studyNumbers.closed],
                      ].map(([label, n]) => (
                        <div key={label}>
                          <div className="rx-home-stat">{countOrDash(n)}</div>
                          <div className="rx-home-muted text-xs">{label}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </Section>
              )}
              {isAdmin && <PatientQueuesCard showPft={PFT_IMPORT_ENABLED} />}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
